import './ui/style.css';
import * as THREE from 'three';
import { REGIONS, TILE, project, unproject, regionAt, motorParks } from '../shared/geo.js';
import { loadMap, nearestSettlement, findPlace } from './world/mapdata.js';
import { Chunks } from './world/chunks.js';
import { Cells, streamMode } from './world/cells.js';
import { Landmarks } from './world/landmarks.js';
import { buildFarTerrain, groundMaterial } from './world/terrain.js';
import { treeMaterial, setTreeRenderer } from './world/trees.js';
import { vehicleShape } from './game/vehicles.js';
import { wallAtlas, windowGlowAtlas, zincTexture, tarTexture, earthTexture, detailTexture, wallNormalAtlas, zincNormal, tarNormal, earthNormal, lampPoolTexture } from './world/textures.js';
import { Sky } from './world/sky.js';
import { gfxMode, usesNodes, createRenderer, backendName } from './world/renderer.js';
import { MeshLambertNodeMaterial, MeshPhongNodeMaterial, RenderPipeline } from 'three/webgpu';
import { GpuTimer } from './world/gputime.js';
import { Physics } from './physics/physics.js';
import { Wetness, applyWetness } from './world/weather.js';
import { setGroundTint, disposeGround } from './world/terrain.js';
import { texture, uniform, step, attribute, vec3, pass } from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { Cascades } from './world/shadows.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { GTAOPass } from 'three/examples/jsm/postprocessing/GTAOPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { Rain } from './world/rain.js';
import { Sound } from './sound.js';
import { Labels } from './world/labels.js';
import { Input } from './game/input.js';
import { Player, OrbitCamera } from './game/player.js';
import { VEHICLES, Vehicles } from './game/vehicles.js';
import { Builder } from './game/build.js';
import { Jobs } from './game/jobs.js';
import { animateCharacter } from './game/character.js';
import { Hud } from './ui/hud.js';
import { Minimap } from './ui/minimap.js';
import { Panels } from './ui/panels.js';
import { Touch } from './ui/touch.js';
import { BigMap } from './ui/bigmap.js';
import { savedProfile, askProfile, forgetProfile, askAge, ageKnown } from './ui/welcome.js';
import { askPrologue, savedOrigin } from './ui/prologue.js';
import { t as tr, startLang, setLang, LANGS, lang } from '../shared/i18n.js';
import { People } from './game/people.js';
import { Animals } from './game/animals.js';
import { Traffic } from './game/traffic.js';
import { Markets } from './game/markets.js';
import { Roadside } from './game/roadside.js';
import { Story } from './game/story.js';
import { Route } from './game/route.js';
import { Net } from './net.js';
import { ROLES, ECONOMY } from '../shared/content/economy.js';
import { Barks } from './game/barks.js';
import { FOOD } from '../shared/content/food.js';
import { Life } from './game/life.js';

const QUALITY = {
  // radius: tiles each way on the tile path; cellRadius: metres of full detail on the cell path
  // shadowRange: metres of shadow around the player; shadowMap: the shadow map's side in texels
  // proxyRadius: metres to which far tiles show as one low mesh each on the cell path
  // bodies: skinned, animated people near the player (the rest are box figures)
  low: { radius: 2, cellRadius: 320, proxyRadius: 1000, shadows: false, pixelRatio: 1, trees: 'cards', bloom: false, webgpu: false, shadowRange: 0, shadowMap: 0, bodies: 8 },
  medium: { radius: 3, cellRadius: 576, proxyRadius: 1700, shadows: true, pixelRatio: 1.5, trees: true, bloom: true, webgpu: false, shadowRange: 170, shadowMap: 2048, softShadows: false, bodies: 24, cascades: { cascades: 2, mapSize: 2048, maxFar: 300 } },
  high: { radius: 4, cellRadius: 832, proxyRadius: 2600, shadows: true, pixelRatio: 2, trees: true, bloom: true, webgpu: true, shadowRange: 260, shadowMap: 4096, softShadows: true, bodies: 40, cascades: { cascades: 3, mapSize: 2048, maxFar: 520 }, ao: true },
};

class Game {
  constructor() {
    this.canvas = document.getElementById('view');
    this.hud = new Hud(document.getElementById('ui'));
    this.params = new URLSearchParams(location.search);
    startLang(this.params);
    this.timeOffset = 0;
    this.clockMode = this.params.get('clock') === 'real' ? 'real' : 'fast';
    if (!this.params.get('clock')) {
      try {
        if (localStorage.getItem('abakwa-clock') === 'real') this.clockMode = 'real';
      } catch {
        // no storage: the fast day
      }
    }
    this.frames = []; // recent frame times in ms, for the p95 figure
    // stalls: main-thread tasks over 50 ms (a frame missed outright), counted since the page opened
    this.longTasks = 0;
    this.longest = 0;
    try {
      new PerformanceObserver((list) => {
        for (const e of list.getEntries()) {
          this.longTasks++;
          if (e.duration > this.longest) this.longest = Math.round(e.duration);
        }
      }).observe({ type: 'longtask', buffered: true });
    } catch {
      // not every browser reports long tasks
    }
    this.perfOn = this.params.get('perf') === '1';
    this.toldServer = 0;
    this.drone = false;
    this.droneSpeed = 60;
    this.ready = false;
  }

  // The moment the world shows: the real time, unless a test time was set.
  // The game's clock. With the fast pace (the default) a whole day passes every two real hours,
  // the same for everyone; ?time= pins an hour for testing; 'real' follows the Bamenda clock.
  now() {
    const real = Date.now();
    if (this.timeOffset || this.clockMode === 'real') return new Date(real + this.timeOffset);
    const day = 86400000;
    return new Date(real - (real % day) + ((real * 12) % day));
  }

  // Shows the world as it would be at a given Bamenda clock time today, e.g. "14:30".
  setTime(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    if (!Number.isFinite(h)) return;
    const real = new Date();
    const utcMinutes = real.getUTCHours() * 60 + real.getUTCMinutes();
    const wanted = (h - 1) * 60 + (m || 0); // Bamenda is one hour ahead of UTC
    this.timeOffset = (wanted - utcMinutes) * 60000;
  }

  // Who the player is: the name in the address if there is one (used when
  // testing), else what they chose before, else the first screen asks.
  async profile() {
    if (ageKnown() !== 'adult' && this.params.get('adult') !== '1') {
      this.hud.loadingNode.hidden = true;
      await askAge(document.getElementById('ui'));
      this.hud.loadingNode.hidden = false;
    }
    const saved = savedProfile();
    const named = this.params.get('name');
    if (named) return { name: named.slice(0, 20), look: saved ? saved.look : { skin: 0, shirt: 0, trousers: 0 }, start: this.params.get('geo') === '1' ? 'here' : 'home' };
    if (saved && !this.params.get('new')) return saved;
    this.hud.loadingNode.hidden = true;
    const chosen = await askProfile(document.getElementById('ui'), saved);
    // a brand-new player lives a childhood first, alone; the choices go to the server with hello
    if (!saved && !savedOrigin() && this.params.get('prologue') !== '0') this.origin = await askPrologue(document.getElementById('ui'), chosen.name);
    this.hud.loadingNode.hidden = false;
    // asked for by P (?new=1): once answered, the address goes back to normal
    const q = new URLSearchParams(location.search);
    if (q.has('new')) {
      q.delete('new');
      history.replaceState(null, '', q.toString() ? '?' + q.toString() : location.pathname);
    }
    return chosen;
  }

  async start() {
    this.me = await this.profile();
    // the region asked for, else where the player really is (if that is on one of the maps),
    // else the one last travelled to, else Bamenda
    let regionId = this.params.get('region');
    if (regionId && !REGIONS[regionId]) throw new Error(`There is no region called "${regionId}".`);
    const live = await this.liveLocation();
    if (live) {
      const at = regionAt(live.lat, live.lon);
      if (at && (!regionId || regionId === at)) {
        regionId = at;
        const [x, z] = project(REGIONS[at], live.lat, live.lon);
        this.liveSpawn = { x, z, name: REGIONS[at].name };
      } else this.farAway = true;
    }
    if (!regionId) {
      try {
        regionId = localStorage.getItem('abakwa-region');
      } catch {
        // no storage: start in Bamenda
      }
    }
    if (!REGIONS[regionId]) regionId = 'northwest';
    try {
      this.map = await loadMap(regionId, (f, text) => this.hud.loading(f * 0.6, text));
    } catch (err) {
      // the region's map is not on this server: fall back to Bamenda
      if (regionId === 'northwest') throw err;
      regionId = 'northwest';
      this.map = await loadMap(regionId, (f, text) => this.hud.loading(f * 0.6, text));
    }
    this.regionId = regionId;
    this.region = REGIONS[regionId];
    // the live position must fall on a built tile, or the street would be empty ground
    if (this.liveSpawn) {
      const key = `${Math.floor(this.liveSpawn.x / TILE)}_${Math.floor(this.liveSpawn.z / TILE)}`;
      if (!(this.map.meta.tiles || []).includes(key)) {
        this.liveSpawn = null;
        this.farAway = true;
      }
    }
    this.parks = [];
    this.routes = [];
    const quality = QUALITY[this.params.get('q')] || QUALITY.medium;
    this.quality = quality;
    if (this.params.get('time')) this.setTime(this.params.get('time'));

    // ?gfx=legacy|new|new-gl: the old renderer, or the new one on WebGPU / forced WebGL2
    this.gfx = gfxMode(this.params);
    this.stream = streamMode(this.params);
    // ?physics=1: the driven taxi or bike is simulated by Rapier in a worker (cell streaming only)
    this.physics = this.params.get('physics') === '1' && this.stream === 'cells' ? new Physics() : null;
    const nodes = usesNodes(this.gfx);
    const renderer = await createRenderer(this.canvas, quality, this.gfx, this.params.get('grade') !== '0');
    // far trees as baked cards: the legacy renderer bakes them (?impostors=0 keeps full shapes)
    if (!nodes && quality.trees && this.params.get('impostors') !== '0') setTreeRenderer(renderer);
    this.renderer = renderer;
    this.backend = backendName(renderer, this.gfx);
    this.gpu = new GpuTimer(renderer, this.gfx === 'legacy');

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(58, 1, 0.3, 45000);
    this.sky = new Sky(this.scene, this.region, nodes, { range: quality.shadowRange, mapSize: quality.shadowMap });
    // cascaded shadows on the legacy renderer where the tier has shadows (?csm=0 keeps the one map)
    this.cascades = !nodes && quality.shadows && quality.cascades && this.params.get('csm') !== '0' ? new Cascades(this.scene, this.camera, this.sky, quality.cascades) : null;
    this.rain = new Rain(this.scene);
    this.sound = new Sound();
    // ?rain=0.8 shows the world in rain whatever the real weather is (for testing)
    if (this.params.get('rain')) {
      this.forcedRain = Math.max(0, Math.min(1, Number(this.params.get('rain')) || 0));
      this.sky.setWeather({ rain: this.forcedRain, cloud: 0.9 });
    }

    const offset = (factor, units) => ({ polygonOffset: true, polygonOffsetFactor: factor, polygonOffsetUnits: units });
    // roads and roofs take a highlight from the sun (Phong), which rain turns into a wet sheen
    const Phong = nodes ? MeshPhongNodeMaterial : THREE.MeshPhongMaterial;
    // relief from the photos' normal maps (the art bible: plaster matt, zinc corrugated, tar grainy)
    const relief = (normalMap, k) => ({ normalMap, normalScale: new THREE.Vector2(k, k) });
    const materials = {
      nodes,
      detail: detailTexture(),
      walls: nodes
        ? new MeshPhongNodeMaterial({ map: wallAtlas(), vertexColors: true, side: THREE.DoubleSide, specular: 0x000000, shininess: 2, ...relief(wallNormalAtlas(), 0.55) })
        : new THREE.MeshPhongMaterial({ map: wallAtlas(), vertexColors: true, side: THREE.DoubleSide, specular: 0x000000, shininess: 2, emissive: 0xffd9a0, emissiveMap: windowGlowAtlas(), emissiveIntensity: 0, ...relief(wallNormalAtlas(), 0.55) }),
      roofs: new Phong({ map: zincTexture(), vertexColors: true, side: THREE.DoubleSide, specular: 0x1a1a1a, shininess: 18, ...relief(zincNormal(), 1.0) }),
      tar: new Phong({ map: tarTexture(), vertexColors: true, specular: 0x080808, shininess: 8, ...offset(-2, -2), ...relief(tarNormal(), 0.7) }),
      earth: new Phong({ map: earthTexture(), vertexColors: true, specular: 0x000000, shininess: 5, ...offset(-1, -1), ...relief(earthNormal(), 0.8) }),
      water: new THREE.MeshLambertMaterial({ vertexColors: true, transparent: true, opacity: 0.86, ...offset(-1, -1) }),
      // the streetlights' pools on the ground, additive, faded in with the dark (nightLights)
      pools: new THREE.MeshBasicMaterial({ map: lampPoolTexture(), vertexColors: true, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide }),
    };
    // ?wet=1 soaks the streets whatever the weather (for screenshots and measurement)
    this.wetness = new Wetness(this.params.get('wet') !== null ? Math.max(0, Math.min(1, Number(this.params.get('wet')) || 0)) : null);
    applyWetness(this.wetness.level, materials, setGroundTint);
    // each window carries a number (`lit`); those above this cut glow at night
    this.windowCut = { value: 2 };
    this.windowGlow = { value: 0 }; // how brightly, 0 by day
    if (nodes) {
      this.windowCut = uniform(2);
      this.windowGlow = uniform(0);
      materials.walls.emissiveNode = texture(windowGlowAtlas()).rgb.mul(vec3(1, 0.85, 0.63)).mul(this.windowGlow).mul(step(this.windowCut, attribute('lit', 'float')));
    } else materials.walls.onBeforeCompile = (shader) => {
      shader.uniforms.uCut = this.windowCut;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float lit;\nvarying float vLit;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvLit = lit;');
      shader.fragmentShader = shader.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform float uCut;\nvarying float vLit;')
        .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance *= step(uCut, vLit);');
    };
    this.materials = materials;
    this.power = true;
    // a lamp carried by the player and the headlights of what they drive, both off by day
    this.torch = new THREE.PointLight(0xffd9a0, 0, 30, 2);
    this.beam = new THREE.SpotLight(0xfff0c8, 0, 80, 0.5, 0.6, 2);
    this.scene.add(this.torch, this.beam, this.beam.target);
    this.chunks = this.stream === 'cells' ? new Cells(this.scene, this.map, materials) : new Chunks(this.scene, this.map, materials);
    this.chunks.radius = this.stream === 'cells' ? quality.cellRadius : quality.radius;
    this.chunks.trees = !!quality.trees;
    this.chunks.treeFar = quality.trees === 'cards' ? 0 : 150; // Low draws every tree as a card
    this.chunks.physics = this.physics;
    this.chunks.proxyRadius = quality.proxyRadius || 0;
    // streetlights only in the town centre (Commercial Avenue and around), as the art bible says
    this.chunks.lamps = { x: this.region.spawn.x, z: this.region.spawn.z, radius: 1400 };
    // hand-made landmarks stand in for their map buildings on both streaming paths
    this.landmarks = new Landmarks(this.scene, this.map.heightmap, this.map.regionId);
    await this.landmarks.ready;
    this.chunks.skips = this.landmarks.skips;
    this.labels = new Labels(this.scene, this.map.places, this.map.heightmap);
    this.scene.add(buildFarTerrain(this.map.heightmap, this.map.meta.bounds, this.chunks.hole, nodes));
    // the new renderer draws through a post-processing chain: for now bloom, so lit windows and
    // headlights glow at night (phones on the low setting skip it)
    this.post = null;
    this.composer = null;
    // High on the legacy renderer: ground-truth ambient occlusion through a post chain (?ao=0 skips it)
    if (!nodes && quality.ao && this.params.get('ao') !== '0') {
      const composer = new EffectComposer(renderer);
      composer.addPass(new RenderPass(this.scene, this.camera));
      const gtao = new GTAOPass(this.scene, this.camera, window.innerWidth, window.innerHeight);
      gtao.updateGtaoMaterial({ radius: 1.8, distanceExponent: 1, thickness: 1, scale: 1.3, samples: 12, distanceFallOff: 1, screenSpaceRadius: false });
      gtao.blendIntensity = 0.85;
      composer.addPass(gtao);
      composer.addPass(new OutputPass());
      this.post = composer;
      this.composer = composer;
      renderer.info.autoReset = false; // the passes would reset the counts the perf overlay reads
    }
    if (nodes && quality.bloom && this.params.get('post') !== '0') {
      const scenePass = pass(this.scene, this.camera);
      const colour = scenePass.getTextureNode('output');
      this.post = new RenderPipeline(renderer);
      this.post.outputNode = colour.add(bloom(colour, 0.3, 0.5, 0.85));
    }
    // a region that reaches the coast has the sea: the height data is zero there
    if (this.map.meta.height.min < 1) {
      const b = this.map.meta.bounds;
      const reach = 12000; // the sea runs on past the southern edge of the map
      const sea = new THREE.Mesh(
        new THREE.PlaneGeometry(b.x1 - b.x0, b.z1 - b.z0 + reach).rotateX(-Math.PI / 2),
        new THREE.MeshLambertMaterial({ color: 0x2c6f9a, transparent: true, opacity: 0.92 })
      );
      sea.position.set((b.x0 + b.x1) / 2, 0.35, (b.z0 + b.z1 + reach) / 2);
      sea.receiveShadow = true;
      this.scene.add(sea);
    }
    this.minimap = new Minimap(document.getElementById('ui'), this.chunks, this.map.places);

    const look = this.me.look;
    this.player = new Player(this.scene, this.map, this.chunks, look);
    this.people = new People(this.scene, this.map.heightmap, this.chunks, quality.bodies || 24);
    this.barks = new Barks(this.scene);
    // energy: walking and running wear you down, street food brings you back (Lagos Life's loop)
    this.energy = 100;
    try {
      const saved = Number(localStorage.getItem('abakwa-energy'));
      if (Number.isFinite(saved) && saved > 0) this.energy = Math.min(100, saved);
    } catch {
      // not remembered
    }
    this.energySaved = 0;
    if (!nodes && this.params.get('cards') !== '0') this.people.useRenderer(this.renderer);
    this.player.characters = this.people.characters;
    this.animals = new Animals(this.scene, this.map.heightmap, this.chunks);
    this.setupOnline(look);
    this.vehicles = new Vehicles(this.scene, this.map, this.chunks);
    this.vehicles.physics = this.physics;
    this.traffic = new Traffic(this.scene, this.map, this.vehicles);
    this.vehicles.obstacles = (x, z, list) => this.vehicles.collect(x, z, this.traffic.collect(x, z, list));
    this.player.obstacles = (x, z, list) => this.vehicles.collect(x, z, this.traffic.collect(x, z, list));
    this.traffic.people = this.people;
    this.markets = new Markets(this.scene, this.map, this.chunks);
    this.markets.landmarks = this.landmarks;
    this.roadside = new Roadside(this.scene, this.map, this.traffic, this.vehicles, this.hud);
    this.warmUp();
    this.settlementPeople = 1500;
    this.inTheWay = [];
    const mapBuilt = this.chunks.onTileBuilt;
    const mapRemoved = this.chunks.onTileRemoved;
    this.chunks.onTileBuilt = (t) => {
      mapBuilt(t);
      this.vehicles.addTile(t);
      this.traffic.addTile(t);
      this.markets.addTile(t);
      this.roadside.addTile(t);
    };
    this.chunks.onTileRemoved = (t) => {
      mapRemoved(t);
      this.vehicles.removeTile(t);
      this.traffic.removeTile(t);
      this.markets.removeTile(t);
      this.roadside.removeTile(t);
    };
    this.orbit = new OrbitCamera(this.camera, this.map.heightmap);
    this.input = new Input(this.canvas);
    // the action dock's buttons act as keys for one frame
    this.hud.onPassed = () => this.sound && this.sound.chime && this.sound.chime();
    this.hud.onAction = (k) => {
      if (k === 'click') this.input.clicked = true;
      else {
        this.input.press(k);
        setTimeout(() => this.input.release(k), 120);
      }
    };
    this.bigmap = new BigMap(document.getElementById('ui'), this.map, (place) => this.setGoal(place), () => this.clearGoal());
    this.bigmap.minimap = this.minimap;
    this.bigmap.onClose = () => {
      this.input.enabled = true;
    };

    const spawn = this.spawnPoint();
    this.player.place(spawn.x, spawn.z);
    this.orbit.yaw = spawn.yaw || 0;
    this.orbit.target.set(spawn.x, this.player.y + 1.4, spawn.z);

    // a phone or tablet gets a stick and buttons; so does anything the moment it is touched
    const touch = () => {
      if (!this.touch) this.touch = new Touch(document.getElementById('ui'), this.input, this.hud);
    };
    if (matchMedia('(pointer: coarse)').matches || this.params.get('touch')) touch();
    else window.addEventListener('touchstart', touch, { once: true });
    window.addEventListener('resize', () => this.resize());
    this.resize();
    this.last = performance.now();
    this.started = this.last;
    requestAnimationFrame((t) => this.frame(t));
  }

  spawnPoint() {
    const p = this.params;
    const name = p.get('place');
    if (name) {
      const found = findPlace(this.map.places, name);
      if (found) return { x: found.x, z: found.z + 12 };
    }
    if (p.get('x') && p.get('z')) return { x: Number(p.get('x')), z: Number(p.get('z')) };
    if (this.liveSpawn && p.get('by') !== 'coach') return { x: this.liveSpawn.x, z: this.liveSpawn.z, yaw: Math.PI };
    const start = this.region.spawn;
    // nothing says where: the player wakes up at their house once the server says where it is
    if (p.get('by') !== 'coach') this.wakeAtHome = true;
    if (p.get('by') === 'coach') {
      // a coach sets its passengers down at the motor park nearest the middle of town
      let park = null;
      for (const q of motorParks(this.region, this.map.places)) {
        if (!park || Math.hypot(q.x - start.x, q.z - start.z) < Math.hypot(park.x - start.x, park.z - start.z)) park = q;
      }
      if (park) return { x: park.x + 6, z: park.z + 6 };
    }
    return start;
  }

  // The player's house gets a board with their name, like the named places, found by the map
  // search as 'my house'.
  markHome(home) {
    if (home.hx === undefined) return;
    if (this.homePlace) {
      if (this.homePlace.x === home.hx && this.homePlace.z === home.hz) return;
      const k = this.map.places.indexOf(this.homePlace);
      if (k >= 0) this.map.places.splice(k, 1);
    }
    this.homePlace = { n: `${this.me.name}: my house`, k: 'home', x: home.hx, z: home.hz };
    this.map.places.push(this.homePlace);
  }

  // Where the player really is, from the browser, when nothing in the address says where to
  // start and the player allows it. Null when refused, unknown, insecure (geolocation needs
  // https or localhost), switched off with ?geo=0, or slower than six seconds.
  liveLocation() {
    const p = this.params;
    if (p.get('geo') === '0' || p.get('place') || (p.get('x') && p.get('z')) || p.get('by') === 'coach') return Promise.resolve(null);
    if (p.get('geo') !== '1' && !(this.me && this.me.start === 'here')) return Promise.resolve(null);
    if (!navigator.geolocation || !window.isSecureContext) return Promise.resolve(null);
    this.hud.loading(0.02, 'Finding where you are');
    return new Promise((resolve) => {
      let done = false;
      const finish = (v) => {
        if (!done) resolve(v);
        done = true;
      };
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => finish({ lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy }),
          () => finish(null),
          { enableHighAccuracy: false, timeout: 6000, maximumAge: 300000 }
        );
      } catch {
        finish(null);
      }
      setTimeout(() => finish(null), 7000);
    });
  }

  // Moves the player to a named place. Returns false if nothing matches.
  goto(name) {
    const found = findPlace(this.map.places, name);
    if (!found) return false;
    this.teleport(found.x, found.z + 12);
    return true;
  }

  teleport(x, z) {
    this.player.place(x, z);
    this.orbit.target.set(x, this.player.y + 1.4, z);
  }

  resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.renderer.setSize(w, h, false);
    if (this.composer) this.composer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  // Puts one speck of every material in front of the camera while the loading screen is up, so
  // every shader (and its shadow variant) is compiled then and not in the middle of a drive, when a
  // kind of thing first comes into view. Measured 8 October 2026: a first compile cost up to half a
  // second on an ordinary laptop GPU.
  warmUp() {
    const group = new THREE.Group();
    const geo = new THREE.BoxGeometry(0.05, 0.05, 0.05).toNonIndexed();
    const n = geo.attributes.position.count;
    geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(n * 3).fill(1), 3));
    geo.setAttribute('lit', new THREE.BufferAttribute(new Float32Array(n).fill(1), 1));
    const identity = new THREE.Matrix4();
    const white = new THREE.Color(1, 1, 1);
    const add = (material, instanced = false, tinted = false) => {
      let mesh;
      if (instanced) {
        mesh = new THREE.InstancedMesh(geo, material, 1);
        mesh.setMatrixAt(0, identity);
        if (tinted) mesh.setColorAt(0, white);
      } else mesh = new THREE.Mesh(geo, material);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.frustumCulled = false;
      group.add(mesh);
    };
    const m = this.materials;
    add(m.walls);
    add(m.roofs);
    add(m.tar);
    add(m.earth);
    add(m.water);
    add(treeMaterial(), true);
    add(this.markets.material);
    add(this.people.material, true, true);
    add(vehicleShape('taxi').material, true);
    add(this.traffic.material, true);
    add(this.traffic.material, true, true);
    const speck = new THREE.DataTexture(new Uint8Array([120, 150, 80, 255]), 1, 1);
    speck.needsUpdate = true;
    add(groundMaterial(speck, m.detail, !!m.nodes));
    group.position.set(this.player.x, this.player.y - 2, this.player.z);
    this.scene.add(group);
    this.warm = group;
  }

  coolDown() {
    if (!this.warm) return;
    this.scene.remove(this.warm);
    this.warm.children[0].geometry.dispose();
    disposeGround(this.warm.children[this.warm.children.length - 1].material);
    this.warm = null;
  }

  // Draw calls, triangles, memory and the slowest-but-5% frame time of the last five seconds.
  get perf() {
    const now = performance.now();
    if (this.perfCache && now - this.perfCacheAt < 250) return this.perfCache;
    this.perfCacheAt = now;
    const sorted = this.frames.slice().sort((a, b) => a - b);
    const p95 = sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))] : 0;
    const info = this.renderer ? this.renderer.info : null;
    // the new renderer counts render passes in "calls" and draws in "drawCalls"; the old one has only "calls"
    return (this.perfCache = {
      calls: info ? (info.render.drawCalls !== undefined ? info.render.drawCalls : info.render.calls) : 0,
      triangles: info ? info.render.triangles : 0,
      geometries: info ? info.memory.geometries : 0,
      textures: info ? info.memory.textures : 0,
      p95: Math.round(p95 * 10) / 10,
      fps: sorted.length ? Math.round(1000 / (sorted.reduce((a, b) => a + b, 0) / sorted.length)) : 0,
      tier: this.params.get('q') || 'medium',
      gfx: (this.gfx || 'legacy') + (this.backend ? '/' + this.backend : ''),
      stream: this.stream,
      longTasks: this.longTasks,
      longest: this.longest,
      gpu: this.gpu && this.gpu.ms !== null ? Math.round(this.gpu.ms * 10) / 10 : null,
      physics: this.physics ? (this.physics.ready ? Math.round(this.physics.lag * 10) / 10 : -1) : null,
      wet: Math.round(this.wetness.level * 100) / 100,
    });
  }

  // Once a minute, tell the server how the game runs on this machine (no personal data).
  report(t) {
    if (!this.ready || t - this.toldServer < 60000 || !this.frames.length) return;
    this.toldServer = t;
    const p = this.perf;
    const body = JSON.stringify({ p95: p.p95, fps: p.fps, calls: p.calls, tier: p.tier, gfx: p.gfx, dpr: window.devicePixelRatio || 1, w: window.innerWidth, h: window.innerHeight, touch: !!this.touch, ua: navigator.userAgent.slice(0, 160) });
    try {
      if (!navigator.sendBeacon || !navigator.sendBeacon('telemetry', new Blob([body], { type: 'application/json' }))) fetch('telemetry', { method: 'POST', body, headers: { 'Content-Type': 'application/json' }, keepalive: true }).catch(() => {});
    } catch {
      // telemetry is best effort
    }
  }

  frame(t) {
    const dt = Math.min(0.1, (t - this.last) / 1000);
    this.frames.push(t - this.last);
    if (this.frames.length > 300) this.frames.shift();
    this.last = t;
    const input = this.input;

    this.handleKeys(input);
    if (input.hit('v')) {
      this.drone = !this.drone;
      if (this.drone) this.dronePos = this.camera.position.clone();
      this.hud.toast(this.drone ? 'Drone view. Press Enter to land here, V to go back.' : 'Back on foot');
    }

    this.orbit.look(input.lookX, input.lookY, this.drone ? 0 : input.wheel);
    let focus;
    if (this.drone) {
      focus = this.flyDrone(dt);
    } else {
      const car = this.handleVehicle(input, dt);
      if (!car) this.player.update(dt, input.move(), input.down('Shift'), input.hit(' '), this.orbit.yaw);
      this.orbit.update(this.player.x, this.player.y + 1.4, this.player.z, dt, this.chunks);
      focus = this.player.char.group.position;
    }

    const complete = this.chunks.update(focus.x, focus.z);
    this.worldComplete = complete;
    this.sky.update(this.now(), focus, this.chunks.viewDistance);
    if (this.cascades) this.cascades.update();
    this.labels.update(focus.x, focus.z, this.camera);
    this.nightLights(focus);
    this.people.update(dt, focus.x, focus.z);
    this.wearDown(dt);
    {
      const { hour, weekday } = this.clock();
      this.barks.update(dt, this.people, this.player.x, this.player.z, this.camera, { hour, weekday, rain: this.sky.weather.rain, power: this.power, night: this.sky.daylight < 0.4 });
    }
    this.markets.update();
    this.animals.update(dt, focus.x, focus.z, t, this.sky.daylight);
    // traffic stops for the player and for the other players
    this.inTheWay.length = 0;
    this.inTheWay.push(this.player);
    for (const p of this.people.players.values()) this.inTheWay.push(p);
    this.traffic.update(dt, focus.x, focus.z, this.inTheWay);
    this.roadside.update(this.drone ? null : this.vehicles.driving, this.player.x, this.player.z, t);
    this.builder.update(this.camera, input, focus.x, focus.z, t);
    this.jobs.update(this.vehicles.driving, t);
    this.minimap.marks = [...this.jobs.marks(), ...this.story.marks(), ...(this.life.state && this.life.state.career ? [{ x: this.life.state.career.place.x, z: this.life.state.career.place.z, color: '#3b9eff' }] : []), ...(this.life.home ? [{ x: this.life.home.x, z: this.life.home.z, color: '#f3a6c4' }] : [])];
    if (this.goal) {
      this.minimap.marks.push({ x: this.goal.x, z: this.goal.z, color: '#e78fb3' });
      if (this.route.active) {
        const off = this.route.nearest(this.player.x, this.player.z).off;
        if (off > 45) this.askRoute();
        if (t - (this.routeTextAt || 0) > 500) {
          this.routeTextAt = t;
          this.hud.setRoute(this.route.text(this.player.x, this.player.z), this.route.nextTurn(this.player.x, this.player.z));
        }
      } else this.askRoute();
      if (Math.hypot(this.goal.x - this.player.x, this.goal.z - this.player.z) < 12) {
        this.hud.toast(tr('You have arrived at {place}', { place: this.goal.n }), { kind: 'success' });
        this.route.clear();
        this.hud.setRoute('');
        this.goal = null;
        this.goalBeam.visible = false;
      }
    }
    this.talkToServer(t, focus);
    this.rain.update(dt, this.camera, this.sky.weather.rain);
    applyWetness(this.wetness.update(dt, this.sky.weather.rain), this.materials, setGroundTint);
    const driven = this.drone ? null : this.vehicles.driving;
    this.sound.update({
      dt,
      driving: !!driven,
      bike: !!driven && driven.type === 'bike',
      speed: driven ? Math.abs(driven.speed) : 0,
      walking: !driven && !this.drone && this.player.grounded ? this.player.speed : 0,
      footPhase: this.player.footPhase == null ? null : this.player.footPhase,
      rain: this.sky.weather.rain,
      night: 1 - this.sky.daylight,
      traffic: this.trafficNear(focus),
      generator: !this.power && this.sky.daylight < 0.6 && this.people.npcs.size > 8,
      water: this.chunks.wet && this.chunks.wet(focus.x, focus.z, 14) ? 1 : 0,
      people: this.people.npcs.size,
    });
    this.gpu.begin();
    if (this.composer) this.renderer.info.reset();
    if (this.post) this.post.render();
    else this.renderer.render(this.scene, this.camera);
    this.gpu.end();
    this.minimap.draw(focus.x, focus.z, this.orbit.yaw, this.drone ? this.orbit.yaw : this.player.yaw);

    if (!this.ready) {
      const waited = t - this.started;
      this.hud.loading(0.6 + 0.4 * Math.min(1, waited / 4000), 'Building the streets');
      if (complete || waited > 12000) {
        this.ready = true;
        this.hud.loaded();
        this.coolDown();
      }
    }
    this.updateHud(t, focus);
    if (this.perfOn) this.hud.setPerf(this.perf);
    this.report(t);
    input.endFrame();
    requestAnimationFrame((n) => this.frame(n));
  }

  // Lit windows, the player's lamp and headlights once the sun is down.
  nightLights(focus) {
    const dark = 1 - this.sky.daylight;
    const { hour } = this.clock();
    // most homes are lit in the evening and few in the small hours; in a power
    // cut only the odd house with a generator or a solar panel has light
    let cut = hour >= 18 && hour < 22 ? 0.3 : hour >= 22 ? 0.3 + (hour - 22) * 0.3 : hour < 4.5 ? 0.92 : 0.6;
    if (!this.power) cut = 0.97;
    this.windowCut.value = cut;
    if (this.materials.pools) this.materials.pools.opacity = dark * (this.power ? 0.6 : 0.04);
    if (this.materials.nodes) this.windowGlow.value = dark * 1.5;
    else this.materials.walls.emissiveIntensity = dark * 1.5;
    const car = this.drone ? null : this.vehicles.driving;
    this.vehicles.setNight(dark);
    this.torch.intensity = this.drone ? 0 : dark * (car ? 8 : 16);
    this.torch.position.set(focus.x, focus.y + 2.4, focus.z);
    this.beam.intensity = car ? dark * 1200 : 0;
    if (car) {
      const fx = Math.sin(car.yaw);
      const fz = Math.cos(car.yaw);
      this.beam.position.set(car.x + fx * 1.8, car.y + 1.1, car.z + fz * 1.8);
      this.beam.target.position.set(car.x + fx * 16, this.map.heightmap.heightAt(car.x + fx * 16, car.z + fz * 16), car.z + fz * 16);
    }
  }

  // What the server says about the settlement the player is in: its herds and its electricity.
  setSettlement(m) {
    if (m.herd) {
      this.animals.setHerd(m.herd);
      this.settlementPeople = m.herd[0];
    }
    if (m.petrol !== undefined) this.roadside.setFuel(!m.petrol);
    if (m.power === undefined) return;
    const on = !!m.power;
    if (on !== this.power && this.sky.daylight < 0.6) this.hud.toast(on ? `${m.place} has light` : `No light in ${m.place}: a power cut`, 4500);
    if (on !== this.power && !on) {
      this.sound.cut();
      // the nearest two people say what everyone says
      let said = 0;
      for (const n of this.people.npcs.values()) {
        if (said >= 2 || !n.body) continue;
        this.barks.say(n, this.barks.pick(n, { hour: this.clock().hour, weekday: this.clock().weekday, rain: 0, power: false, night: true }));
        said++;
      }
    }
    this.power = on;
  }

  // ---- online: townspeople, other players, chat ----

  setupOnline(look) {
    const ui = document.getElementById('ui');
    this.net = new Net();
    this.panels = new Panels(ui, (type, payload) => this.net.send({ t: type, ...payload }));
    this.panels.onOpenApp = (id) => this.openPhone(id);
    this.panels.onMap = () => this.toggleMap();
    this.panels.onSetting = (id) => this.setting(id);
    const net = this.net;
    net.on('welcome', (m) => {
      this.people.me = m.id;
      if (this.liveSpawn) this.hud.toast(`You start where you really are, in the ${this.liveSpawn.name}.`, 6000);
      else if (this.farAway) this.hud.toast('You are far from the North West and South West, so you start at your house in Bamenda.', 7000);
      this.parks = m.parks || [];
      if (this.vehicles) this.vehicles.parkCoaches(this.parks);
      this.routes = m.routes || [];
      this.start = m.start;
      this.njangi = !!m.njangi;
      if (m.fresh && m.start) {
        const card = m.start === 'bushfaller'
          ? `You be bushfaller pikin: your people for Germany send money home. You start with ${ECONOMY.startBushfaller.toLocaleString('en-US')} FCFA and ${ECONOMY.remittance.toLocaleString('en-US')} every Saturday. Your self-contain for Nkwen na ${(this.life.rent || ECONOMY.weeklyRent).toLocaleString('en-US')} a week.`
          : `You be quarter boy: ${ECONOMY.startQuarter.toLocaleString('en-US')} FCFA, your two legs, and a seat in Mama Ngum's njangi (${ECONOMY.njangiShare.toLocaleString('en-US')} every Saturday, ${(ECONOMY.njangiShare * ECONOMY.njangiMembers).toLocaleString('en-US')} when your turn comes). Your room for Ntarikon na ${(this.life.rent || ECONOMY.weeklyRent).toLocaleString('en-US')} a week: go find work.`;
        setTimeout(() => this.hud.toast(card, { kind: 'money', sticky: true }), 6000);
      }
      this.hud.toast('Year ' + m.year + ' in the ' + this.region.name + '. ' + m.people.toLocaleString('en-US') + ' people live here.', 5000);
    });
    // the coach fare is paid: load the other region, arriving at its motor park
    net.on('travel', (m) => {
      try {
        localStorage.setItem('abakwa-region', m.region);
      } catch {
        // no storage: the address below still carries the region
      }
      this.hud.toast('The coach to ' + m.name + ' is leaving. Fare ' + m.fare.toLocaleString('en-US') + ' FCFA.', 6000);
      const q = new URLSearchParams(location.search);
      for (const key of ['place', 'x', 'z']) q.delete(key);
      q.set('region', m.region);
      q.set('by', 'coach');
      setTimeout(() => {
        location.search = q.toString();
      }, 1500);
    });
    net.on('people', (m) => {
      this.people.setTownspeople(m.list, this.jobs.passenger());
      this.setSettlement(m);
    });
    net.on('players', (m) => this.people.setPlayers(m.list));
    net.on('week', (m) => {
      this.jobs.money = m.money;
      const parts = [];
      parts.push(m.rent ? tr('landlord took {n}', { n: m.rent.toLocaleString('en-US') }) : tr('you could not pay the rent, you owe {n}', { n: m.debt.toLocaleString('en-US') }));
      if (m.remit) parts.push(tr('your people sent {n}', { n: m.remit.toLocaleString('en-US') }));
      if (m.share) parts.push(tr('njangi took {n}', { n: m.share.toLocaleString('en-US') }));
      if (m.pot) parts.push(tr('na your turn: the njangi pot of {n} is yours', { n: m.pot.toLocaleString('en-US') }));
      if (m.loan) parts.push(tr('the microfinance took {n}', { n: m.loan.toLocaleString('en-US') }));
      if (m.defaulted) parts.push(tr('you defaulted on the loan: your njangi seat is gone and the rest is debt'));
      this.hud.toast(tr('Saturday: {parts}. You have {money} FCFA.', { parts: parts.join('; '), money: m.money.toLocaleString('en-US') }), { kind: 'money', ms: 9000 });
    });
    net.on('ate', (m) => {
      const food = FOOD[m.item];
      this.jobs.money = m.money;
      if (!food) return;
      this.energy = Math.min(100, this.energy + food.gain);
      this.hud.toast(`You chop ${food.name}.`, 3000);
    });
    net.on('person', (m) => this.panels.showPerson(m.info));
    net.on('family', (m) => {
      this.family = m;
      if (this.panels.kind === 'phone' && this.panels.phoneApp === 'family') this.openPhone('family');
    });
    net.on('married', (m) => {
      this.jobs.money = m.money;
      this.hud.toast(tr('You married {who}. The bride price and the wedding came to {n} FCFA.', { who: m.name, n: m.cost.toLocaleString('en-US') }), { kind: 'money', ms: 8000 });
    });
    net.on('divorced', (m) => this.hud.toast(tr('{who} has gone back to her people.', { who: m.name }), 6000));
    net.on('handedover', (m) => {
      this.jobs.money = m.money;
      this.hud.toast(tr('You go on as {who}. The celebration cost {n} FCFA; the house and the name are yours.', { who: m.name, n: m.cost.toLocaleString('en-US') }), { kind: 'money', ms: 9000 });
    });
    net.on('toast', (m) => this.hud.toast(m.text, 6000));
    net.on('route', (m) => {
      if (!this.goal || m.tx !== this.goal.x || m.tz !== this.goal.z) return;
      if (m.points) {
        this.route.set(m.points, m.metres, this.goal);
        this.minimap.route = m.points;
        this.bigmap.route = m.points;
      } else {
        this.minimap.route = null;
        this.bigmap.route = null;
        this.hud.setRoute(tr('No road leads there; follow the marker'), null);
      }
    });
    net.on('story', (m) => {
      this.news = [m, ...(this.news || [])].slice(0, 20);
      this.hud.toast(tr('News: {text}', { text: m.text }), 9000);
    });
    net.on('town', (m) => this.panels.showTown(m));
    net.on('chat', (m) => this.panels.addChat(m.from, m.text));
    net.on('myplaces', (m) => {
      const names = Object.fromEntries(Object.entries(REGIONS).map(([id, r]) => [id, r.name]));
      this.panels.showPlaces(m, this.regionId, names, (place) => this.showWay(place));
    });
    net.on('live', (m) => this.setLive(m));
    net.on('offline', () => this.hud.toast(tr('Lost the server. The town is quiet until it returns.'), { kind: 'danger', sticky: true }));
    const name = this.me.name;
    // a private key made once in this browser: it is what makes your plots yours
    let token = '';
    try {
      token = localStorage.getItem('abakwa-token') || '';
      if (!token) {
        const bytes = crypto.getRandomValues(new Uint8Array(18));
        token = Array.from(bytes, (b) => 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789'[b % 56]).join('');
        localStorage.setItem('abakwa-token', token);
      }
    } catch {
      // without storage the player builds as a guest for this visit
    }
    this.builder = new Builder(this.scene, this.map, net, this.hud, ui);
    this.builder.physics = this.physics;
    this.player.builder = this.builder;
    this.jobs = new Jobs(this.scene, this.map, net, this.hud);
    this.life = new Life(net, this.hud);
    this.life.onMoney = (money) => {
      this.jobs.money = money;
    };
    this.life.onHome = (vehicles, home) => {
      if (this.vehicles) this.vehicles.parkOwned(vehicles, home);
      this.markHome(home);
      if (this.wakeAtHome) {
        this.wakeAtHome = false;
        this.teleport(home.x, home.z);
        if (home.dx !== undefined) this.orbit.yaw = Math.atan2(home.dx, home.dz);
        this.started = performance.now();
      }
    };
    net.on('acted', (m) => {
      const gives = Object.entries(m.gives || {}).filter(([, v]) => v > 0).map(([n, v]) => `+${v} ${n}`).join(', ');
      this.hud.toast(`${m.what === 'sleep' ? 'You slept well.' : 'Done.'} ${gives}${m.learned && m.learned.gain > 0.5 ? ` · ${m.learned.skill} +${m.learned.gain.toFixed(1)}` : ''}`, 4000);
      if (this.panels.kind === 'phone') this.openPhone(this.panels.phoneApp);
    });
    net.on('moved', (m) => this.hud.toast(`You moved. ${m.cost.toLocaleString('en-US')} FCFA paid up front.`, 5000));
    net.on('fired', (m) => this.hud.toast(`You lost your job at ${m.place}: three shifts missed.`, 8000));
    net.on('home', (m) => {
      this.jobs.money = m.money;
      if (this.vehicles.driving) {
        this.vehicles.exit();
        this.player.char.group.visible = true;
      }
      this.teleport(m.x, m.z);
      this.panels.close();
      this.hud.toast(tr('The taxi dropped you at {place}. Fare {n} FCFA.', { place: m.name, n: m.fare.toLocaleString('en-US') }), { kind: 'money', ms: 6000 });
    });
    net.on('achieved', (m) => {
      this.jobs.money = m.money;
      this.hud.toast(tr('Achievement: {name}. {n} FCFA.', { name: t(m.name), n: m.reward.toLocaleString('en-US') }), { kind: 'success', ms: 7000 });
    });
    net.on('event', (m) => {
      this.jobs.money = m.money;
      this.hud.toast(`${m.day}: ${m.text}`, 8000);
    });
    net.on('mayor', (m) => this.hud.toast(`${m.name} won the election and is mayor for two weeks.`, 8000));
    net.on('voted', (m) => this.hud.toast(tr('You voted for {who}.', { who: m.name }), 4000));
    net.on('election', (m) => {
      this.election = m;
      if (this.panels.kind === 'phone' && this.panels.phoneApp === 'council') this.openPhone('council');
    });
    net.on('bought', (m) => {
      this.hud.toast(`${m.name} is yours.`, 4000);
      if (this.panels.kind === 'phone') this.openPhone(this.panels.phoneApp);
    });
    net.on('sold', (m) => this.hud.toast(`Sold for ${m.value.toLocaleString('en-US')} FCFA.`, 4000));
    net.on('loaned', () => this.hud.toast('The microfinance paid 60,000 FCFA into your hand. 12,000 every Saturday for six weeks.', 7000));
    net.on('sent', (m) => this.hud.toast(`Sent ${m.amount.toLocaleString('en-US')} FCFA to ${m.to} (fee ${m.fee}).`, 4500));
    net.on('received', (m) => {
      this.jobs.money = m.money;
      this.hud.toast(`${m.from} sent you ${m.amount.toLocaleString('en-US')} FCFA.`, 6000);
    });
    net.on('business', (m) => {
      this.jobs.money = m.money;
      const text = m.lines.map((l) => `${l.name} ${l.bad ? 'had a bad day: ' : 'brought '}${l.amount.toLocaleString('en-US')}`).join('; ');
      this.hud.toast(`${m.day}: ${text}.`, 8000);
    });
    net.on('hired', (m) => {
      this.hud.toast(`You got the job. Report to ${m.place} in the shift hours and press E to work.`, 7000);
      if (this.panels.kind === 'phone') this.openPhone('jobs');
    });
    net.on('worked', (m) => {
      this.jobs.money = m.money;
      this.hud.toast(`Shift done${m.late ? ' (you were late)' : ''}: ${m.pay.toLocaleString('en-US')} FCFA at ${m.performance}%. ${m.skill} +${m.gain.toFixed(1)}.${m.promoted ? ` Promoted: ${m.promoted}!` : ''}`, 8000);
    });
    this.jobs.onPickup = (job) => {
      const n = this.people.npcs.get(job.person);
      if (n) this.barks.say(n, this.barks.passengerLine(job.to), 4);
    };
    this.jobs.onPaid = (m) => {
      const who = this.people.nearest(this.player.x, this.player.z, 12);
      if (who && m.line) this.barks.say(who, m.line, 4);
    };
    this.story = new Story(this.scene, this.map, net, this.hud, ui);
    // the way to a saved place: a pink beam in the world and a pink dot on the corner map
    this.goal = null;
    const beam = new THREE.CylinderGeometry(2.4, 2.4, 80, 20, 1, true);
    beam.translate(0, 40, 0);
    this.goalBeam = new THREE.Mesh(beam, new THREE.MeshBasicMaterial({ color: 0xe78fb3, transparent: true, opacity: 0.34, side: THREE.DoubleSide, depthWrite: false, fog: false }));
    this.goalBeam.visible = false;
    this.scene.add(this.goalBeam);
    this.route = new Route(this.scene, this.map.heightmap);
    net.connect(name, look, token, this.regionId, this.origin || savedOrigin());
    this.sentPos = 0;
    this.askedNear = 0;
  }

  // Real weather and headlines from the server.
  setLive(m) {
    this.live = m;
    const w = m.weather;
    if (w) {
      // rain is millimetres per quarter hour: 1 mm is already a steady shower
      this.sky.setWeather({ cloud: w.cloud, rain: Math.min(1, w.rain / 1.5), fog: w.code === 45 || w.code === 48 ? 0.75 : 0 });
      if (this.forcedRain !== undefined) this.sky.setWeather({ rain: this.forcedRain, cloud: 0.9 });
      this.weatherText = w.temp + '°C, ' + w.text;
    }
    if (this.panels.kind === 'news') this.panels.showNews(m);
  }

  // The hour (0-24) and weekday (0 Sunday) shown in the world, in Bamenda time.
  clock() {
    const d = new Date(this.now().getTime() + 3600000);
    return { hour: d.getUTCHours() + d.getUTCMinutes() / 60, weekday: d.getUTCDay() };
  }

  talkToServer(t, focus) {
    if (!this.net.online) return;
    if (t - this.sentPos > 100) {
      this.sentPos = t;
      const p = this.player;
      const car = this.vehicles.driving;
      this.net.send({ t: 'pos', x: p.x, y: p.y, z: p.z, yaw: p.yaw, speed: p.speed, veh: car ? car.def.id : 0 });
    }
    if (t - this.askedNear > 2500) {
      this.askedNear = t;
      const { hour, weekday } = this.clock();
      this.net.send({ t: 'near', x: focus.x, z: focus.z, hour, weekday });
    }
  }

  // Opens or closes the full-screen map.
  toggleMap() {
    if (this.bigmap.open) {
      this.bigmap.close();
      return;
    }
    this.input.enabled = false;
    this.input.releasePointer();
    this.panels.close();
    this.bigmap.show({ x: this.player.x, z: this.player.z, yaw: this.player.yaw }, this.minimap.marks);
  }

  handleKeys(input) {
    if (input.hit('Tab')) this.toggleMap();
    if (this.bigmap.open) {
      // the map has the keyboard: only Escape gets through, to close it
      if (input.hit('Escape')) this.bigmap.close();
      return;
    }
    if (input.hit('Escape') && this.panels.open) this.panels.close();
    if (input.hit('e') && this.workNear()) {
      const { hour, weekday } = this.clock();
      this.net.send({ t: 'shift', hour, weekday, date: this.dateText() });
    } else if (input.hit('e')) {
      const who = this.people.nearest(this.player.x, this.player.z, 6);
      if (who) {
        this.net.send({ t: 'person', id: who.id });
        this.barks.reply(who);
      } else this.hud.toast('Nobody is close enough to greet');
    }
    if (input.hit('c') && !this.drone && !this.vehicles.driving) {
      const food = this.foodNear();
      if (!food) this.hud.toast('No food here. Find a kiosk or the market.');
      else if (!this.net.online) this.hud.toast('Buying food needs the server. It is not connected.');
      else this.net.send({ t: 'eat', item: food.item });
    }
    if (input.hit('b') && !this.drone && !this.vehicles.driving) {
      const on = this.builder.toggle();
      this.hud.toast(on ? 'Build mode. Point at the ground and click to place a piece.' : 'Build mode off');
    }
    if (input.hit('j') && !this.drone) {
      if (this.vehicles.driving || this.jobs.job) this.jobs.toggle(this.vehicles.driving, this.clock());
      else this.hud.toast('Get into a taxi or onto a bike first, then press J to find a passenger');
    }
    if (input.hit('g') && !this.drone && !this.vehicles.driving) {
      const route = this.routes[0];
      if (!route) this.hud.toast('No coach runs from here: this server has only one region');
      else if (!this.nearPark()) this.hud.toast('Coaches leave from the motor parks: the blue B on the corner map');
      else this.net.send({ t: 'travel', to: route.to });
    }
    if (input.hit('l')) {
      if (this.panels.kind === 'places') this.panels.close();
      else if (this.net.online) this.net.send({ t: 'myplaces' });
      else this.hud.toast('Your places are kept on the server. It is not connected.');
    }
    if (input.hit('i') && !this.drone) {
      if (this.panels.kind === 'phone') this.panels.close();
      else this.openPhone('apps');
    }
    if (input.hit('p') && !this.drone && input.down('Shift')) {
      // back to the first screen to change name and look
      const q = new URLSearchParams(location.search);
      q.delete('name');
      q.set('new', '1');
      location.search = q.toString();
    }
    if (input.hit('o')) this.hud.toast(this.sound.toggle() ? tr('Sound on') : tr('Sound off'));
    if (input.hit('h') && this.vehicles.driving && !this.drone) this.sound.horn(this.vehicles.driving.type === 'bike');
    if (input.hit('m')) {
      if (!this.story.mission) this.hud.toast('No missions yet: the server is not connected');
      else this.hud.toast(this.story.toggle() ? 'Mission shown' : 'Mission hidden. Press M to show it again.');
    }
    if (input.hit('n')) {
      if (this.panels.kind === 'news') this.panels.close();
      else if (this.live) this.panels.showNews(this.live);
      else this.hud.toast('No news yet: the server is not connected');
    }
    if (input.hit('t')) {
      if (this.panels.kind === 'town') this.panels.close();
      else this.net.send({ t: 'town', x: this.player.x, z: this.player.z });
    }
    if (input.hit('Enter') && !this.drone && this.net.online) {
      input.enabled = false;
      input.releasePointer();
      this.panels.startChat((text) => this.net.send({ t: 'chat', text }), () => {
        input.enabled = true;
      });
    }
  }

  // Shows the way to a spot: a pink dot on the maps and a pink beam in the world.
  setGoal(place) {
    this.goal = place;
    this.goalBeam.position.set(place.x, this.map.heightmap.heightAt(place.x, place.z), place.z);
    this.goalBeam.visible = true;
    this.askRoute(true);
    const far = Math.hypot(place.x - this.player.x, place.z - this.player.z);
    const how = far < 1000 ? tr('{n} m', { n: Math.round(far / 10) * 10 }) : tr('{n} km', { n: (far / 1000).toFixed(1) });
    this.hud.toast(tr('On the way to {place}', { place: place.n }), { kind: 'info', ms: 3500 });
    void how;
  }

  // Asks the server for the way by road to the goal; again when the player has strayed from it.
  askRoute(force = false) {
    if (!this.goal || !this.net.online) return;
    const now = performance.now();
    if (!force && this.routeAskedAt && now - this.routeAskedAt < 4000) return;
    this.routeAskedAt = now;
    this.net.send({ t: 'route', tx: this.goal.x, tz: this.goal.z, id: this.goal.n });
  }

  clearGoal() {
    this.goal = null;
    this.goalBeam.visible = false;
    this.route.clear();
    this.hud.setRoute('');
  }

  // Points the player to one of their saved places, or stops doing so if asked twice.
  showWay(place) {
    if (place.r !== this.regionId) {
      this.hud.toast(`${place.n} is in the ${REGIONS[place.r] ? REGIONS[place.r].name : 'other region'}. Take the coach from a motor park first.`, 5000);
      return;
    }
    if (this.goal && this.goal.code === place.code) {
      this.clearGoal();
      this.goalBeam.visible = false;
      this.hud.toast('No longer showing the way');
      return;
    }
    this.panels.close();
    this.setGoal(place);
  }

  // The workplace's name when standing at it, else null.
  workNear() {
    const k = this.life.state && this.life.state.career;
    if (!k) return null;
    return Math.hypot(k.place.x - this.player.x, k.place.z - this.player.z) < 70 ? k.place.n : null;
  }

  // Today's date in the town's time, for one shift a day.
  dateText() {
    const d = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: this.region.timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
    return parts;
  }

  // Opens the phone on an app, with what it needs to know about here and now.
  // A setting changed from the phone.
  setting(id) {
    if (id === 'sound') {
      this.input.press('o');
      setTimeout(() => this.input.release('o'), 120);
    } else if (id === 'profile') {
      const q = new URLSearchParams(location.search);
      q.set('new', '1');
      location.search = q.toString();
    } else if (id === 'quality') {
      const order = ['low', 'medium', 'high'];
      const q = new URLSearchParams(location.search);
      const now = q.get('q') || 'medium';
      q.set('q', order[(order.indexOf(now) + 1) % order.length]);
      location.search = q.toString();
    } else if (id === 'language') {
      const codes = Object.keys(LANGS);
      setLang(codes[(codes.indexOf(lang()) + 1) % codes.length]);
      location.reload();
      return;
    } else if (id === 'clock') {
      const real = this.clockMode === 'real';
      this.setClockMode(real ? 'fast' : 'real');
    }
    if (this.panels.kind === 'phone') this.openPhone(this.panels.phoneApp);
  }

  openPhone(app) {
    this.panels.settings = { clock: this.clockMode || 'fast', sound: this.sound ? this.sound.on : true, quality: this.params.get('q') || 'medium', language: LANGS[lang()] };
    const { hour, weekday } = this.clock();
    const near = [];
    for (const p of this.map.places) if (Math.hypot(p.x - this.player.x, p.z - this.player.z) < 60 && !near.includes(p.k)) near.push(p.k);
    if (app === 'council' && !this.election) this.net.send({ t: 'election' });
    if (app === 'family' && !this.familyAsked) {
      this.familyAsked = true;
      this.net.send({ t: 'family' });
      setTimeout(() => (this.familyAsked = false), 1500);
    }
    this.panels.showPhone(app, { life: this.life.state, needs: this.life.needs || {}, money: this.jobs.money, rent: this.life.rent || 0, home: this.life.home || { name: '?' }, atHome: this.life.atHome(this.player.x, this.player.z), near, hour, weekday, start: this.start, njangi: this.njangi, election: this.election, player: this.player, known: this.life.known || 0, news: this.news || [], family: this.family });
  }

  // Energy drains with effort and hunger slows the walk; saved now and then.
  wearDown(dt) {
    const driving = !!this.vehicles.driving;
    const moving = !driving && !this.drone && this.player.speed > 0.2;
    const running = moving && this.player.speed > 4;
    const drain = driving ? 0.012 : running ? 0.11 : moving ? 0.05 : 0.012;
    this.energy = Math.max(0, this.energy - drain * dt);
    this.life.update(dt);
    this.player.pace = this.life.needs ? this.life.pace() : this.energy < 10 ? 0.5 : this.energy < 25 ? 0.7 : 1;
    this.energySaved += dt;
    if (this.energySaved > 10) {
      this.energySaved = 0;
      try {
        localStorage.setItem('abakwa-energy', String(Math.round(this.energy)));
      } catch {
        // not remembered
      }
    }
  }

  // What there is to eat where the player stands: at a kiosk, bread; in an open market,
  // puff-puff, or a plate when hungry. { item, name, price, gain } or null.
  foodNear() {
    const x = this.player.x;
    const z = this.player.z;
    const hungry = this.life.needs ? this.life.needs.hunger < 40 : this.energy < 40;
    if (this.markets.at(x, z, 40)) return { item: hungry ? 'meal' : 'puffpuff', ...FOOD[hungry ? 'meal' : 'puffpuff'] };
    if (this.chunks.propsNear && this.chunks.propsNear('kiosk', x, z, 5)) return { item: 'bread', ...FOOD.bread };
    return null;
  }

  // How much traffic is within earshot: 0 for none, 1 for six or more cars within 60 m.
  trafficNear(focus) {
    let n = 0;
    for (const car of this.traffic.cars) if (Math.abs(car.x - focus.x) < 60 && Math.abs(car.z - focus.z) < 60) n++;
    return Math.min(1, n / 6);
  }

  // True if the player stands at a motor park, where coaches can be boarded.
  nearPark() {
    return this.parks.some((k) => Math.hypot(k.x - this.player.x, k.z - this.player.z) < 150);
  }

  // ---- vehicles ----

  // On foot: F gets into the nearest parked vehicle. Driving: F gets out.
  handleVehicle(input, dt) {
    const v = this.vehicles.driving;
    if (!v) {
      if (input.hit('f')) {
        const near = this.vehicles.nearest(this.player.x, this.player.z, 4.5);
        if (near) {
          this.vehicles.enter(near);
          this.orbit.distance = Math.max(this.orbit.distance, 9);
          this.player.char.group.visible = near.type === 'bike';
        } else this.hud.toast('No vehicle close enough');
      }
      return null;
    }
    if (input.hit('f')) {
      const out = this.vehicles.exit();
      this.player.char.group.visible = true;
      animateCharacter(this.player.char, 0, dt);
      this.player.place(out.x, out.z);
      return null;
    }
    this.vehicles.drive(dt, input.move(), input.down(' '));
    const p = this.player;
    p.x = v.x;
    p.z = v.z;
    p.y = v.y;
    p.yaw = v.yaw;
    p.speed = Math.abs(v.speed);
    if (v.type === 'bike') {
      animateCharacter(p.char, 0, dt, true);
      p.char.group.position.set(v.x, v.y + v.def.seat, v.z);
      p.char.group.rotation.set(0, v.yaw, v.lean);
    }
    p.poseBody(dt, v.type === 'bike' ? 'sit' : null);
    // the view swings round behind the vehicle unless the player is looking about
    if (!input.dragging && document.pointerLockElement !== this.canvas && Math.abs(v.speed) > 2) {
      let diff = v.yaw - this.orbit.yaw;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      this.orbit.yaw += diff * Math.min(1, dt * 1.8);
    }
    return v;
  }

  flyDrone(dt) {
    const input = this.input;
    if (input.wheel) this.droneSpeed = Math.max(10, Math.min(600, this.droneSpeed * Math.exp(-input.wheel * 0.001)));
    const move = input.move();
    const speed = this.droneSpeed * (input.down('Shift') ? 4 : 1);
    const yaw = this.orbit.yaw;
    const pitch = this.orbit.pitch;
    const fx = Math.sin(yaw);
    const fz = Math.cos(yaw);
    const p = this.dronePos;
    p.x += (fx * move.z - fz * move.x) * speed * dt;
    p.z += (fz * move.z + fx * move.x) * speed * dt;
    p.y += ((input.down('e') ? 1 : 0) - (input.down('q') ? 1 : 0)) * speed * dt - Math.sin(pitch) * move.z * speed * dt;
    const b = this.map.meta.bounds;
    p.x = Math.max(b.x0, Math.min(b.x1, p.x));
    p.z = Math.max(b.z0, Math.min(b.z1, p.z));
    const floor = this.map.heightmap.heightAt(p.x, p.z) + 2;
    if (p.y < floor) p.y = floor;
    this.camera.position.copy(p);
    const c = Math.cos(pitch);
    this.camera.lookAt(p.x + fx * c, p.y - Math.sin(pitch), p.z + fz * c);
    if (input.hit('Enter')) {
      this.drone = false;
      this.teleport(p.x, p.z);
      this.hud.toast('Landed');
    }
    return p;
  }

  updateHud(t, focus) {
    if (this.hudAt && t - this.hudAt < 250) return;
    this.hudAt = t;
    const near = nearestSettlement(this.map.places, focus.x, focus.z);
    const { hour, weekday } = this.clock();
    const quiet = weekday === 1 && this.region.quietMonday;
    this.traffic.setTime(hour, quiet, this.settlementPeople, this.region.nightBikeBan);
    this.markets.setTime(hour, quiet);
    const [lat, lon] = unproject(this.region, focus.x, focus.z);
    const height = Math.round(this.map.heightmap.heightAt(focus.x, focus.z));
    const car = this.vehicles.driving;
    this.hud.setPlace(near ? near.n : this.region.name, car && !this.drone ? `${Math.round(Math.abs(car.speed) * 3.6)} km/h` : `${height} m ${tr('above sea level')}`);
    const now = this.now();
    const minute = Math.floor(now.getTime() / 60000);
    if (this.clockMinute !== minute) {
      // formatting dates is slow; once a minute is enough
      this.clockMinute = minute;
      const locale = lang() === 'fr' ? 'fr-FR' : 'en-GB';
      this.clockTime = now.toLocaleTimeString('en-GB', { timeZone: this.region.timezone, hour: '2-digit', minute: '2-digit' });
      this.clockDay = now.toLocaleDateString(locale, { timeZone: this.region.timezone, weekday: 'long', day: 'numeric', month: 'long' });
    }
    const time = this.clockTime;
    const day = this.clockDay;
    this.hud.setStatus({ time, day, weather: this.weatherText, money: this.jobs.money, tired: !!(this.life.needs && Math.min(this.life.needs.hunger, this.life.needs.energy) < 25) });
    this.position = { lat, lon };
    if (this.drone) this.hud.setHint(['W A S D', 'fly', 'Q E', 'down, up', 'Shift', 'fast', 'Enter', 'land here', 'V', 'back on foot']);
    else if (this.builder.active) this.hud.setHint(['Click', 'place', 'X', 'remove', 'R', 'turn', 'C', 'colour', '1-9', 'piece', 'B', 'stop building']);
    else if (car) this.hud.setHint(['J', this.jobs.job ? 'give up the ride' : 'find a passenger', 'W S', 'go, brake', 'A D', 'steer', 'Space', 'handbrake', 'H', 'horn', 'Tab', 'map', 'F', 'get out']);
    else {
      const parked = this.vehicles.nearest(this.player.x, this.player.z, 4.5);
      if (parked) {
        this.hud.setHint(['F', 'drive the ' + (VEHICLES[parked.type] ? VEHICLES[parked.type].name : parked.type), 'W A S D', 'walk', 'Shift', 'run', 'T', 'town', 'N', 'news', 'V', 'drone']);
        return;
      }
      const who = this.people.nearest(this.player.x, this.player.z, 6);
      const greet = who ? ['E', 'greet the ' + ROLES[who.role].name.toLowerCase()] : [];
      const food = this.foodNear();
      if (food) greet.push('C', `chop ${food.name}, ${food.price.toLocaleString('en-US')} FCFA`);
      const work = this.workNear();
      if (work) greet.unshift('E', `work your shift at ${work}`);
      if (this.life.atHome(this.player.x, this.player.z)) greet.push('I', 'at home: sleep, bathe, cook');
      else greet.push('I', 'phone');
      if (this.routes[0] && this.nearPark()) greet.push('G', 'coach to ' + this.routes[0].name + ', ' + this.routes[0].fare.toLocaleString('en-US') + ' FCFA');
      this.hud.setHint([...greet, 'W A S D', 'walk', 'Shift', 'run', 'B', 'build', 'T', 'town', 'N', 'news', 'Tab', 'map', 'M', 'mission', 'L', 'my places', 'Enter', 'chat', 'V', 'drone']);
    }
  }
}

window.addEventListener('DOMContentLoaded', () => {
  const game = new Game();
  window.abakwa = game;
  game.start().catch((err) => {
    console.error(err);
    game.hud.fail(err.message);
  });
});
